import React, { useState, useEffect, useRef } from 'react';
import './Messages.css';

const Messages = ({ facultyId, facultyName }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pollInterval, setPollInterval] = useState(null);
  const messagesEndRef = useRef(null);
  
  // State for new conversation modal
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [courses, setCourses] = useState([]);
  const [courseStudents, setCourseStudents] = useState([]);
  const [newConversationForm, setNewConversationForm] = useState({
    course_id: '',
    student_id: '',
    subject: 'Course Query'
  });

  // Fetch conversations
  useEffect(() => {
    fetchConversations();
  }, [facultyId]);

  // Polling for new messages
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.conversation_id);

      // Poll every 5 seconds
      const interval = setInterval(() => {
        fetchMessages(selectedConversation.conversation_id);
      }, 5000);

      setPollInterval(interval);
      return () => clearInterval(interval);
    }
  }, [selectedConversation]);

  // Auto scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        faculty_id: facultyId,
        limit: 50,
      });

      const response = await fetch(`/api/messages/conversations/faculty?${params}`);
      const data = await response.json();

      if (response.ok) {
        setConversations(data.conversations || []);
        console.log('✅ Fetched conversations:', data.conversations?.length || 0);
      } else {
        setError(data.error || 'Failed to fetch conversations');
        console.error('❌ Error fetching conversations:', data.error);
      }
    } catch (err) {
      console.error('❌ Error fetching conversations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await fetch(`/api/messages/conversation/${conversationId}`);
      const data = await response.json();

      if (response.ok) {
        setMessages(data.messages || []);
        console.log(`✅ Fetched ${data.messages?.length || 0} messages`);
        
        // Mark as read
        markConversationAsRead(conversationId);
      }
    } catch (err) {
      console.error('❌ Error fetching messages:', err);
    }
  };

  const markConversationAsRead = async (conversationId) => {
    try {
      await fetch('/api/messages/mark-read', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          reader_id: facultyId,
        }),
      });

      // Update local state to show read status
      setConversations(prev =>
        prev.map(conv =>
          conv.conversation_id === conversationId
            ? { ...conv, unread_count: 0 }
            : conv
        )
      );
    } catch (err) {
      console.error('❌ Error marking conversation as read:', err);
    }
  };

  // Handle opening new conversation modal
  const handleOpenNewConversation = async () => {
    setShowNewConversation(true);
    // Fetch courses for this faculty
    try {
      const response = await fetch(`/api/faculty/${facultyId}/courses`);
      const data = await response.json();
      console.log('📚 Fetched courses:', data.courses?.length || 0);
      setCourses(data.courses || []);
    } catch (err) {
      console.error('❌ Error fetching courses:', err);
    }
  };

  // Handle course selection in new conversation modal
  const handleCourseChange = async (courseId) => {
    setNewConversationForm(prev => ({
      ...prev,
      course_id: courseId,
      student_id: '' // Reset student selection
    }));
    
    if (courseId) {
      // Fetch students for this course
      try {
        const response = await fetch(`/api/courses/${courseId}/students`);
        const data = await response.json();
        
        if (data.success) {
          console.log(`👥 Fetched ${data.count} students for course ${courseId}`);
          setCourseStudents(data.students || []);
        } else {
          console.error('❌ Error fetching students:', data.error);
          setCourseStudents([]);
        }
      } catch (err) {
        console.error('❌ Error fetching students:', err);
        setCourseStudents([]);
      }
    } else {
      setCourseStudents([]);
    }
  };

  // Start conversation with selected student
  const handleStartConversation = async () => {
    if (!newConversationForm.course_id || !newConversationForm.student_id) {
      alert('❌ Please select both course and student');
      return;
    }

    try {
      console.log('🔄 Creating conversation...');
      const response = await fetch('/api/messages/start-conversation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          student_id: newConversationForm.student_id,
          faculty_id: facultyId,
          course_id: newConversationForm.course_id,
          subject: newConversationForm.subject,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('✅ Conversation created:', data.conversation_id);
        
        // Close modal and refresh conversations
        setShowNewConversation(false);
        setNewConversationForm({ course_id: '', student_id: '', subject: 'Course Query' });
        
        // Fetch updated conversations
        await fetchConversations();
        
        // Find and select the new conversation
        setTimeout(() => {
          const newConv = conversations.find(c => c.conversation_id === data.conversation_id);
          if (newConv) {
            setSelectedConversation(newConv);
          }
        }, 500);
      } else {
        console.error('❌ Error creating conversation:', data.error);
        alert(`❌ Error: ${data.error || 'Failed to create conversation'}`);
      }
    } catch (err) {
      console.error('❌ Error starting conversation:', err);
      alert('❌ Failed to create conversation');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!messageInput.trim() || !selectedConversation) {
      return;
    }

    const messageText = messageInput.trim();
    setMessageInput('');

    try {
      console.log('📤 Sending message to conversation:', selectedConversation.conversation_id);
      const response = await fetch('/api/messages/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: selectedConversation.conversation_id,
          sender_id: facultyId,
          sender_role: 'faculty',
          message_text: messageText,
        }),
      });

      if (response.ok) {
        console.log('✅ Message sent successfully');
        // Refresh messages
        fetchMessages(selectedConversation.conversation_id);
      } else {
        const data = await response.json();
        console.error('❌ Failed to send message:', data.error);
        setError('Failed to send message');
      }
    } catch (err) {
      console.error('❌ Error sending message:', err);
      setError(err.message);
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.student_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.course_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;

    return date.toLocaleDateString();
  };

  return (
    <div className="messages-container">
      {/* Left Sidebar - Conversations List */}
      <div className="conversations-panel">
        <div className="panel-header">
          <h2>💬 Messages</h2>
          <button 
            className="btn-new-message"
            onClick={handleOpenNewConversation}
            title="Start new conversation"
          >
            ✏️
          </button>
          <span className="badge-count">{conversations.filter(c => c.unread_count > 0).length}</span>
        </div>

        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Search student or course..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        {loading && !conversations.length ? (
          <div className="loading-skeleton">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="skeleton-item" />
            ))}
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="empty-conversations">
            <div className="empty-icon">💬</div>
            <p>No conversations yet</p>
            <p className="empty-hint">Click ✏️ to start a new conversation</p>
          </div>
        ) : (
          <div className="conversations-list">
            {filteredConversations.map(conv => (
              <div
                key={conv.conversation_id}
                className={`conversation-item ${
                  selectedConversation?.conversation_id === conv.conversation_id ? 'active' : ''
                } ${conv.unread_count > 0 ? 'unread' : ''}`}
                onClick={() => setSelectedConversation(conv)}
              >
                <div className="conversation-avatar">
                  {conv.student_name.charAt(0).toUpperCase()}
                </div>

                <div className="conversation-info">
                  <div className="info-header">
                    <h4>{conv.student_name}</h4>
                    <span className="timestamp">
                      {formatDate(conv.last_message_at)}
                    </span>
                  </div>

                  <p className="course-badge">{conv.course_name}</p>
                  <p className="last-message">{conv.last_message || 'No messages yet'}</p>
                </div>

                {conv.unread_count > 0 && (
                  <div className="unread-badge">{conv.unread_count}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Conversation Modal */}
      {showNewConversation && (
        <div className="modal-overlay" onClick={() => setShowNewConversation(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📝 Start New Conversation</h2>
              <button 
                className="close-btn" 
                onClick={() => setShowNewConversation(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Select Course:</label>
                <select
                  value={newConversationForm.course_id}
                  onChange={(e) => handleCourseChange(e.target.value)}
                  className="select-field"
                >
                  <option value="">-- Choose a course --</option>
                  {courses.map(course => (
                    <option key={course.course_id} value={course.course_id}>
                      {course.course_name} ({course.course_code})
                    </option>
                  ))}
                </select>
              </div>

              {newConversationForm.course_id && (
                <div className="form-group">
                  <label>Select Student:</label>
                  <select
                    value={newConversationForm.student_id}
                    onChange={(e) => setNewConversationForm(prev => ({
                      ...prev,
                      student_id: e.target.value
                    }))}
                    className="select-field"
                  >
                    <option value="">-- Choose a student --</option>
                    {courseStudents.map(student => (
                      <option key={student.student_id} value={student.student_id}>
                        {student.student_name} ({student.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Subject (Optional):</label>
                <input
                  type="text"
                  value={newConversationForm.subject}
                  onChange={(e) => setNewConversationForm(prev => ({
                    ...prev,
                    subject: e.target.value
                  }))}
                  className="input-field"
                  placeholder="e.g., Doubt about Topic X"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowNewConversation(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleStartConversation}
                disabled={!newConversationForm.course_id || !newConversationForm.student_id}
              >
                Start Conversation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Right Panel - Chat Window */}
      <div className="chat-panel">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="chat-header">
              <div className="header-info">
                <h3>{selectedConversation.student_name}</h3>
                <p>{selectedConversation.course_name}</p>
              </div>
              <div className="header-actions">
                <button className="btn-icon" title="Info">
                  ℹ️
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="messages-area">
              {messages.length === 0 ? (
                <div className="empty-chat">
                  <div className="empty-icon">📝</div>
                  <h4>Start a conversation</h4>
                  <p>Be the first to send a message</p>
                </div>
              ) : (
                messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`message-bubble ${msg.sender_role === 'faculty' ? 'sent' : 'received'
                      }`}
                  >
                    <div className="message-content">
                      <p>{msg.message_text}</p>
                      <span className="message-time">
                        {formatDate(msg.created_at)}
                      </span>
                    </div>
                    {msg.sender_role === 'faculty' && msg.is_read && (
                      <span className="read-indicator">✓✓</span>
                    )}
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="message-input-form">
              <input
                type="text"
                placeholder="Type your message..."
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                className="message-input"
              />
              <button
                type="submit"
                className="btn-send"
                disabled={!messageInput.trim()}
              >
                Send
              </button>
            </form>
          </>
        ) : (
          <div className="no-conversation">
            <div className="no-conv-icon">💭</div>
            <h3>No conversation selected</h3>
            <p>Select a conversation from the left to start messaging</p>
            <button 
              className="btn-new-conv"
              onClick={handleOpenNewConversation}
            >
              ✏️ Start New Conversation
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
